/** Date helpers over ISO YYYY-MM-DD strings. All arithmetic is in UTC so a date never shifts by time zone. */

export function isoToday(): string {
  return new Date().toISOString().slice(0, 10);
}

export function addDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export function addMonths(yyyymm: string, months: number): string {
  const [y, m] = yyyymm.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + months, 1));
  return d.toISOString().slice(0, 7);
}

export function monthOf(iso: string): string {
  return iso.slice(0, 7);
}

export function monthLabel(yyyymm: string): string {
  const [y, m] = yyyymm.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

export function dayLabel(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
}

export function weekdayShort(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' });
}

/**
 * Which quarter a Tuesday week belongs to — the Friday rule (D15): a week
 * belongs to the quarter holding its Friday, so a week straddling a
 * quarter boundary lands where most of its days are. Shared, because three
 * pages had three answers and two of them disagreed here.
 */
/** The Tuesday that opens the week a date falls in — weeks run Tuesday to Monday. */
export function tuesdayOf(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  // 0=Sun..6=Sat; Tuesday is 2, so step back to the most recent Tuesday.
  return addDays(iso, -((d.getUTCDay() - 2 + 7) % 7));
}

/**
 * The quarter today falls in, by the platform's Tuesday weeks (D17) — not by calendar month.
 * Today is snapped to its Tuesday first, because `quarterOfWeek` answers for a WEEK and a
 * Monday in early January belongs to the week that opened in December.
 */
export function currentQuarter(): string {
  return quarterOfWeek(tuesdayOf(isoToday()));
}

/** N quarters forward or back, for the ‹ › on the schedule pages. */
export function shiftQuarter(quarter: string, by: number): string {
  const [y, q] = quarter.split('-Q').map(Number);
  const idx = y * 4 + (q - 1) + by;
  return `${Math.floor(idx / 4)}-Q${(idx % 4) + 1}`;
}

export function quarterOfWeek(week: string): string {
  const friday = addDays(week, 3);
  const [y, m] = friday.split('-').map(Number);
  return `${y}-Q${Math.floor((m - 1) / 3) + 1}`;
}

/**
 * Every Tuesday week in a quarter.
 *
 * Derived by asking `quarterOfWeek` week by week rather than computing the boundaries
 * again: the Friday rule (D15) puts a straddling week wherever most of its days are, so a
 * quarter can open in the previous calendar month and close in the next. Three pages once
 * had three answers to this and two disagreed; there is one answer, and this is it.
 */
export function quarterWeeks(quarter: string): string[] {
  const [y, q] = quarter.split('-Q').map(Number);
  let t = addDays(tuesdayOf(`${y}-${String((q - 1) * 3 + 1).padStart(2, '0')}-01`), -14);
  const out: string[] = [];
  for (let i = 0; i < 24; i += 1, t = addDays(t, 7)) if (quarterOfWeek(t) === quarter) out.push(t);
  return out;
}

/** The days a quarter actually covers: its first Tuesday to its last Monday. */
export function quarterSpan(quarter: string): { from: string; to: string } | null {
  const weeks = quarterWeeks(quarter);
  if (!weeks.length) return null;
  return { from: weeks[0], to: addDays(weeks[weeks.length - 1], 6) };
}

/** "Jan 5" — the month and day, without the weekday, for the ends of a range. */
export function monthDay(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

/** "Jan 5 – Apr 5", or just the quarter's name if it somehow has no weeks. */
export function quarterDates(quarter: string): string | null {
  const span = quarterSpan(quarter);
  return span ? `${monthDay(span.from)} – ${monthDay(span.to)}` : null;
}

/** "Jan" — the month alone, for a grid column that needs to say which one it is in. */
export function monthShort(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' });
}
